/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import Text from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import styles from "./styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTranslation } from "react-i18next";

import axios from "api/configApi";

const LIMIT = 50;

interface ActivityTabProps {
  userId?: string;
}

const ActivityTab = React.memo((props: ActivityTabProps) => {
  const { userId } = props;
  const classes = styles();
  const translate = useTranslation().t;
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [skip, setSkip] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchActivities(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchActivities = async (reset = false) => {
    if (reset) setSkip(0);
    const currentSkip = reset ? 0 : skip;
    try {
      if (reset) {
        setLoading(true);
        setActivities([]);
      } else {
        setLoadingMore(true);
      }
      const res = await axios.get("/log-activities", {
        params: {
          limit: LIMIT,
          skip: currentSkip,
          ...(userId ? { userId } : {}),
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      if (reset) {
        setActivities(data);
      } else {
        setActivities((prev) => [...prev, ...data]);
      }
      setHasMore(data.length >= LIMIT);
      setSkip(currentSkip + data.length);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load activities", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) fetchActivities(false);
  };

  useEffect(() => {
    if (loading || loadingMore || !hasMore || activities.length === 0) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handleLoadMore();
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, activities.length]);

  const renderActivities = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 16 }}>
          <CircularProgress />
        </div>
      );
    }

    if (!loading && activities.length === 0) {
      return (
        <p>
          <i>{translate("NO_ACTIVITIES_YET")}</i>
        </p>
      );
    }

    return (
      <>
        {activities.map((activity) => (
          <div key={activity.id} style={{ marginBottom: 12 }}>
            <Text variant="caption" color="textSecondary">
              {activity.createdAt
                ? new Date(activity.createdAt).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                  })
                : ""}
            </Text>
            <Text variant="body2">{activity.note}</Text>
          </div>
        ))}
        {hasMore && activities.length > 0 && (
          <div ref={loadMoreRef} style={{ minHeight: 1, padding: 8 }}>
            {loadingMore && (
              <div style={{ textAlign: "center" }}>
                <CircularProgress size={24} />
              </div>
            )}
          </div>
        )}
        {!hasMore && activities.length > 0 && (
          <div style={{ textAlign: "center", marginTop: 16 }} className="text-gray-500">
            <Text variant="body2" style={{ fontStyle: "italic" }}>
              {translate("NO_ADDITIONAL_HISTORY")}
            </Text>
          </div>
        )}
      </>
    );
  };

  return (
    <Container className={classes.root}>
      {renderActivities()}

    </Container>
  );
});

export default ActivityTab;
